import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  SearchOutlined,
  DashboardOutlined,
  MessageOutlined,
  SettingOutlined,
  FileTextOutlined,
  BookOutlined,
  QuestionCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { Logo, Icon } from '@brand';
import { getBrandConfig } from '../../brands/brand-registry';
import { useUserContext } from '../UserContext/UserContext';
import './Sidebar.css';

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useUserContext();
  const [isMobile, setIsMobile] = useState(false);

  // Get brand configuration
  const brandId = import.meta.env.VITE_BRAND_ID;
  if (!brandId) {
    throw new Error('VITE_BRAND_ID environment variable is required');
  }
  const brandConfig = getBrandConfig(brandId);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Auto-collapse on mobile
      if (mobile && !collapsed) {
        onCollapse(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collapsed, onCollapse]);

  const menuItems: MenuProps['items'] = [
    {
      key: '/home',
      icon: <HomeOutlined />,
      label: 'Home',
      onClick: () => {
        navigate('/home');
        if (isMobile) onCollapse(true);
      },
    },
    {
      key: '/explore',
      icon: <SearchOutlined />,
      label: 'Explore Data',
      onClick: () => {
        navigate('/explore');
        if (isMobile) onCollapse(true);
      },
    },
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => {
        navigate('/dashboard');
        if (isMobile) onCollapse(true);
      },
    },
    {
      key: '/conversations',
      icon: <MessageOutlined />,
      label: 'Conversations',
      onClick: () => {
        navigate('/conversations');
        if (isMobile) onCollapse(true);
      },
    },
    {
      type: 'divider',
    },
    {
      key: '/configure',
      icon: <SettingOutlined />,
      label: 'Configure',
      onClick: () => {
        navigate('/configure');
        if (isMobile) onCollapse(true);
      },
    },
    {
      key: '/prompts',
      icon: <FileTextOutlined />,
      label: 'Prompts',
      onClick: () => {
        navigate('/prompts');
        if (isMobile) onCollapse(true);
      },
    },
    {
      key: '/glossary',
      icon: <BookOutlined />,
      label: 'Glossary',
      onClick: () => {
        navigate('/glossary');
        if (isMobile) onCollapse(true);
      },
    },
    {
      key: '/suggested-queries',
      icon: <QuestionCircleOutlined />,
      label: 'Suggested Queries',
      onClick: () => {
        navigate('/suggested-queries');
        if (isMobile) onCollapse(true);
      },
    },
  ];

  // Don't render sidebar menu if user is not logged in
  if (!userProfile) {
    return null;
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && !collapsed && (
        <div 
          className="sidebar-overlay" 
          onClick={() => onCollapse(true)}
        />
      )}
      
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}>
        {/* Logo section */}
        <div className="sidebar-logo" onClick={() => navigate('/home')}>
          {collapsed ? (
            <Tooltip title={brandConfig.appName} placement="right">
              <div className="logo-collapsed">
                <Icon width={32} height={32} />
              </div>
            </Tooltip>
          ) : (
            <div className="logo-expanded">
              <Logo width={180} variant="full" />
              {/* <span className="app-name">{brandConfig.appName}</span> */}
            </div>
          )}
        </div>

        {/* Navigation menu */}
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          className="sidebar-menu"
          inlineCollapsed={collapsed}
        />

        {/* Collapse toggle */}
        <div className="sidebar-toggle" onClick={() => onCollapse(!collapsed)}>
          <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </Tooltip>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
